/**
 * DEMO ORGANIZATIONS ONLY — one two-way switch, at the top of the Tap'N Win
 * page: is the demo being shown ON LOCATION or AT HOME? One tap each way, any
 * number of times. It is saved on the demo organization, so every way of
 * opening the demo card follows it. A real venue never sees it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';

const MODES = [['bar', 'On location'], ['home', 'At home']];

export const GoalDemoExperienceBar = ({ organizationId }) => {
    const [experience, setExperience] = useState(null);
    const [demo, setDemo] = useState(false);
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get('/goal-game/admin/demo-experience',
                { params: { organization_id: organizationId } });
            setDemo(!!res.data.demo);
            setExperience(res.data.experience || null);
        } catch (_e) { setDemo(false); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const choose = async (key) => {
        if (key === experience || busy) return;
        setBusy(key);
        try {
            const res = await api.put('/goal-game/admin/demo-experience',
                { organization_id: organizationId, experience: key });
            setExperience(res.data.experience);
            toast.success(key === 'home' ? 'Demo is now at home'
                                         : 'Demo is now on location');
        } catch (_e) {
            toast.error('Could not switch the demo');
        } finally { setBusy(''); }
    };

    if (!demo) return null;

    return (
        <div data-testid="gg-demo-experience-switch"
             className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 px-4 py-3">
            <span className="text-sm font-semibold" data-testid="gg-demo-experience-label">
                Demo experience
            </span>
            <div role="group" aria-label="Demo experience"
                 className="inline-flex items-center gap-1 rounded-lg bg-white/70 dark:bg-black/30 p-1">
                {MODES.map(([key, label]) => {
                    const on = experience === key;
                    return (
                        <button key={key} type="button" onClick={() => choose(key)}
                                aria-pressed={on} disabled={!!busy}
                                data-testid={`gg-demo-experience-${key}`}
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                    on ? 'bg-amber-500 text-white shadow'
                                       : 'text-muted-foreground hover:bg-amber-100 dark:hover:bg-amber-900/30'}`}>
                            {busy === key && <Loader2 size={14} className="animate-spin" />}
                            {label}
                        </button>
                    );
                })}
            </div>
            <span className="text-xs text-muted-foreground" data-testid="gg-demo-experience-hint">
                Every way of opening the demo card follows this. Reset demo does not change it.
            </span>
        </div>
    );
};
